// Lalive header behaviour (design round 3): desktop dropdowns, mega-menu
// height (K18), shrinking header, mobile menu, search panel.
// Split out of lalive.js on 2026-10-05. Classic script, loaded deferred on
// every page from layout/theme.liquid; each part guards against running twice.

(() => {
  if (window.__lvMenu) return;
  window.__lvMenu = true;

  const px = (el, name) => parseFloat(getComputedStyle(el).getPropertyValue(name)) || 0;
  const desktop = window.matchMedia('(min-width: 750px)');

  // A submenu is "small" (280 px dropdown) when it has no column headings.
  const isSmall = (submenu) => !submenu.querySelector('.mega-menu__link--parent');

  // ---------------------------------------------------------------------------
  // 1. Desktop menus
  //
  // Horizon opens every submenu as a full-width panel and grows the header by
  // --submenu-height / --full-open-header-height. Small dropdowns must not grow
  // the header: after Horizon sets the heights (same task as aria-expanded),
  // this MutationObserver callback runs as a microtask and zeroes them, then
  // anchors the box under its label via --lv-dd-left.
  //
  // K18: right after load, the mega menu's content is still being hydrated, so
  // Horizon measured 263 px while the content grew to 548 px and names/prices
  // were cut off (2026-10-05). A ResizeObserver re-applies the height while the
  // menu is open. Skipped for small dropdowns.
  // ---------------------------------------------------------------------------

  // The open item is the one whose disclosure says aria-expanded="true".
  // Not [data-active]: Horizon keeps data-active on the previous submenu until
  // its close animation ends, so while switching Ürünler -> Lalive Dünyası two
  // submenus are "active" and the first (Ürünler) won; the page stayed dimmed
  // and the small dropdown grew the header.
  const activeSubmenu = (menu) =>
    menu.querySelector(
      '.menu-list__list-item:has(> .menu-list__trigger [aria-expanded="true"]) > .menu-list__submenu'
    );

  const placeDropdown = (submenu) => {
    const item = submenu.closest('.menu-list__list-item');
    const parent = submenu.offsetParent;
    if (!item || !parent) return;
    const label = item.querySelector('.menu-list__link') || item;
    const left = label.getBoundingClientRect().left - parent.getBoundingClientRect().left - 20;
    submenu.style.setProperty('--lv-dd-left', `${Math.max(0, Math.round(left))}px`);
  };

  const reconcile = (menu) => {
    const header = menu.closest('#header-component, header-component');
    const submenu = activeSubmenu(menu);
    const open = Boolean(header && submenu && menu.getAttribute('aria-expanded') === 'true');
    // Page dims under the mega menu only (CSS cannot nest :has() to know this).
    document.documentElement.classList.toggle('lv-mega-open', open && desktop.matches && !isSmall(submenu));
    if (!open || !desktop.matches) return;
    if (isSmall(submenu)) {
      header.style.setProperty('--submenu-height', '0px');
      header.style.setProperty('--full-open-header-height', '0px');
      placeDropdown(submenu);
    }
  };

  const onResize = (entries) => {
    for (const { target: submenu } of entries) {
      const menu = submenu.closest('header-menu');
      const header = submenu.closest('#header-component, header-component');
      if (!menu || !header) continue;
      if (submenu !== activeSubmenu(menu) || menu.getAttribute('aria-expanded') !== 'true') continue;
      if (submenu.closest('[slot="overflow"]')) continue; // "More" menu sizes itself differently
      if (isSmall(submenu)) continue;
      const next = submenu.offsetHeight;
      const current = px(header, '--submenu-height');
      if (!next || Math.abs(next - current) < 2) continue;
      const visible = px(header, '--full-open-header-height') - current;
      header.style.setProperty('--submenu-height', `${next}px`);
      if (visible > 0) header.style.setProperty('--full-open-header-height', `${next + visible}px`);
    }
  };

  // Esc and a click outside close an open desktop menu (design note). Horizon
  // listens for Escape on <header-menu> only, so a menu opened by hover (focus
  // still on the page) ignored it. Hand the key to Horizon's own handler, which
  // closes with force and returns focus to the disclosure.
  const closeOpenMenu = (except) => {
    document.querySelectorAll('header-menu[aria-expanded="true"]').forEach((menu) => {
      if (except && menu.contains(except)) return;
      menu.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: false }));
    });
  };

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    if (event.target instanceof Element && event.target.closest('header-menu, dialog')) return;
    closeOpenMenu(null);
  });

  document.addEventListener('pointerdown', (event) => {
    if (!(event.target instanceof Element) || event.target.closest('dialog')) return;
    closeOpenMenu(event.target);
  });

  // Horizon re-activates the item under the *last known* pointer position once
  // the pointer rests (#reconcilePointerTarget dispatches a synthetic
  // pointerenter). Its position stops updating when a menu closes, so after
  // Ürünler -> Create Your Routine the stale event re-opened Ürünler for
  // ~130 ms and closed it again: the flicker Samet saw (10-06). Synthetic
  // enters are honoured only where the pointer really is; real ones always.
  // On window, capture phase: Horizon's component.js listens on document
  // (capture) and is registered first, so a document listener ran too late.
  const pointer = { x: -1, y: -1 };
  window.addEventListener('pointermove', (event) => { pointer.x = event.clientX; pointer.y = event.clientY; }, { capture: true, passive: true });
  window.addEventListener('pointerenter', (event) => {
    if (event.isTrusted || pointer.x < 0) return;
    const item = event.target instanceof Element ? event.target : null;
    if (!item || !item.matches('header-menu .menu-list__list-item')) return;
    const under = document.elementFromPoint(pointer.x, pointer.y);
    if (under && item.contains(under)) return;
    event.stopImmediatePropagation();
  }, true);

  // Menu aim (10-06): on the way from "Ürünler" to the right side of its mega
  // panel the pointer crosses the neighbouring labels (Create Your Routine,
  // Self-Care Club). Horizon treated that as switching menus and closed the
  // panel: Samet "could not get down" to the links. Horizon's safety box only
  // covers the strip under the label, not the diagonal. While the mega menu is
  // open, leaving/entering top-level items is not acted on until the pointer
  // rests for AIM_MS; then: inside the open item (its panel) -> stay open; on
  // another label -> that one opens; elsewhere -> close.
  const AIM_MS = 170;
  let aimTimer = 0;
  const topItem = (el) => (el instanceof Element ? el.closest('header-menu .menu-list__list-item') : null);
  const openMegaItem = () =>
    document.documentElement.classList.contains('lv-mega-open')
      ? document.querySelector('header-menu .menu-list__list-item:has(> .menu-list__trigger [aria-expanded="true"])')
      : null;
  const settleAim = () => {
    clearTimeout(aimTimer);
    aimTimer = setTimeout(() => {
      aimTimer = 0;
      const mega = openMegaItem();
      if (!mega) return;
      const under = document.elementFromPoint(pointer.x, pointer.y);
      if (under && mega.contains(under)) return;
      const other = topItem(under);
      if (other) other.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      else mega.dispatchEvent(new PointerEvent('pointerleave', { bubbles: false }));
    }, AIM_MS);
  };
  ['pointerleave', 'pointerenter'].forEach((type) => {
    window.addEventListener(type, (event) => {
      if (!event.isTrusted || !desktop.matches) return;
      const li = event.target instanceof Element && event.target.matches('header-menu .menu-list__list-item') ? event.target : null;
      if (!li) return;
      const mega = openMegaItem();
      if (!mega) return;
      const leavingSideways = type === 'pointerleave' && topItem(event.relatedTarget) && topItem(event.relatedTarget) !== li;
      if (aimTimer || (li === mega && leavingSideways)) {
        event.stopImmediatePropagation();
        settleAim();
        return;
      }
      // From another label into the open panel: not a reason to close it.
      if (type === 'pointerleave' && li !== mega && topItem(event.relatedTarget) === mega) event.stopImmediatePropagation();
    }, true);
  });

  // While the cart drawer is open (non-modal, it keeps focus inside itself), a
  // press on a menu link moved focus to the link, the drawer pulled it straight
  // back, Horizon closed the menu on that blur, and the click then landed on the
  // header: "Yeni Gelenler" did nothing (Samet, 10-06). A mouse press in the
  // menu does not move focus while the drawer is open; the click still fires.
  document.addEventListener('mousedown', (event) => {
    if (event.button !== 0 || !(event.target instanceof Element) || !event.target.closest('header-menu')) return;
    if (!document.querySelector('#cart-drawer dialog[open]')) return;
    event.preventDefault();
  }, true);

  const resizeObserver = new ResizeObserver(onResize);

  const watchMenus = () => {
    document.querySelectorAll('header-menu').forEach((menu) => {
      menu.querySelectorAll('.menu-list__submenu').forEach((submenu) => {
        if (submenu.dataset.lvWatched) return;
        submenu.dataset.lvWatched = '1';
        resizeObserver.observe(submenu);
      });
      if (menu.dataset.lvWatched) return;
      menu.dataset.lvWatched = '1';
      new MutationObserver(() => reconcile(menu)).observe(menu, {
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-expanded', 'data-active'],
      });
    });
  };

  // "Diğer" ("More") must never show: blocks/_header-menu sets the overflow
  // minimum to the number of top-level items, so one overflowing item switches
  // the header to the mobile menu. But overflow-list can measure before
  // header.js listens (seen at 800 px on first load: menu style stayed "menu"
  // with "Diğer" until the next resize). Re-send Horizon's own event then.
  const syncMenuStyle = () => {
    const header = document.querySelector('#header-component');
    const list = header && header.querySelector('overflow-list');
    if (!list || header.dataset.menuStyle !== 'menu' || !list.hasAttribute('minimum-reached')) return;
    list.dispatchEvent(new CustomEvent('overflowMinimum', { bubbles: true, detail: { minimumReached: true } }));
  };
  let syncTimer = null;
  window.addEventListener('resize', () => {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncMenuStyle, 300);
  });
  window.addEventListener('load', () => {
    syncMenuStyle();
    setTimeout(syncMenuStyle, 1000);
  });

  // ---------------------------------------------------------------------------
  // 2. Shrinking header: html.lv-scrolled past 116 px (announcement 40 +
  // header 76). Off again below 60 px, so the 16 px the header loses when it
  // shrinks cannot flip the state back. Horizon scrolls .page-wrapper on wide
  // screens and the document on small ones; a capturing listener hears both.
  // ---------------------------------------------------------------------------

  const ON = 116;
  const OFF = 60;
  let ticking = false;

  const scrollTop = () => {
    const wrapper = document.querySelector('.page-wrapper');
    return Math.max(
      window.scrollY || 0,
      document.scrollingElement ? document.scrollingElement.scrollTop : 0,
      wrapper ? wrapper.scrollTop : 0
    );
  };

  const updateScrolled = () => {
    ticking = false;
    const y = scrollTop();
    const root = document.documentElement;
    if (y > ON) root.classList.add('lv-scrolled');
    else if (y < OFF) root.classList.remove('lv-scrolled');
  };

  document.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(updateScrolled);
    },
    { capture: true, passive: true }
  );

  // ---------------------------------------------------------------------------
  // 3. Mobile menu (snippets/lv-mobile-menu): native <dialog>, one accordion
  // open at a time, page scroll locked while open.
  // ---------------------------------------------------------------------------

  const lock = (on) => document.documentElement.classList.toggle('lv-lock', on);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-lv-mm-open]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('aria-controls'));
      if (!dialog || dialog.open) return;
      dialog.showModal();
      opener.setAttribute('aria-expanded', 'true');
      lock(true);
      return;
    }

    const closer = event.target.closest('[data-lv-mm-close]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }

    const toggle = event.target.closest('[data-lv-mm-toggle]');
    if (toggle) {
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      const list = toggle.closest('.lv-mm__list');
      list?.querySelectorAll('[data-lv-mm-toggle][aria-expanded="true"]').forEach((other) => {
        if (other === toggle) return;
        other.setAttribute('aria-expanded', 'false');
        const panel = document.getElementById(other.getAttribute('aria-controls'));
        if (panel) panel.hidden = true;
      });
      toggle.setAttribute('aria-expanded', String(open));
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (panel) panel.hidden = !open;
    }
  });

  const bindMobileDialog = () => {
    const dialog = document.getElementById('lv-mm');
    if (!dialog || dialog.dataset.lvBound) return;
    dialog.dataset.lvBound = '1';
    dialog.addEventListener('close', () => {
      lock(false);
      document.querySelectorAll('[data-lv-mm-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    });
    // Tablet side sheet: a click on the dimmed backdrop lands on the <dialog>
    // itself (the bar and body fill the sheet), so it closes the menu.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    // Wider than the drawer breakpoint: close (Horizon shows the desktop menu).
    desktop.addEventListener('change', (e) => {
      if (e.matches && dialog.open && document.querySelector('#header-component[data-menu-style="menu"]')) dialog.close();
    });
  };

  // ---------------------------------------------------------------------------
  // 4. Search panel (snippets/lv-search-panel + sections/lv-predictive-search)
  // ---------------------------------------------------------------------------

  const DEBOUNCE = 220;

  const bindSearch = () => {
    const dialog = document.querySelector('[data-lv-search]');
    if (!dialog || dialog.dataset.lvBound) return;
    dialog.dataset.lvBound = '1';

    const input = dialog.querySelector('[data-lv-search-input]');
    const results = dialog.querySelector('[data-lv-search-results]');
    const empty = dialog.querySelector('[data-lv-search-empty]');
    const clear = dialog.querySelector('[data-lv-search-clear]');
    const url = dialog.dataset.suggestUrl || '/search/suggest';
    const section = dialog.dataset.section || 'lv-predictive-search';
    const placeholderDesk = input.getAttribute('placeholder');
    const placeholderMob = input.dataset.placeholderMobile || placeholderDesk;
    let timer = null;
    let controller = null;
    let lastQuery = '';

    const setPlaceholder = () => input.setAttribute('placeholder', desktop.matches ? placeholderDesk : placeholderMob);

    const showEmpty = () => {
      results.hidden = true;
      results.innerHTML = '';
      if (empty) empty.hidden = false;
      clear.hidden = true;
      lastQuery = '';
    };

    const fetchResults = async (q) => {
      controller?.abort();
      controller = new AbortController();
      const params = new URLSearchParams({
        q,
        section_id: section,
        'resources[type]': 'product,collection,article,page,query',
        'resources[limit]': '4',
        'resources[limit_scope]': 'each',
        'resources[options][prefix]': 'last',
      });
      try {
        const response = await fetch(`${url}?${params}`, { signal: controller.signal });
        if (!response.ok) return showFallback(q);
        const html = await response.text();
        if (input.value.trim() !== q) return; // stale
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const root = doc.querySelector(`#shopify-section-${section}`) || doc.body;
        results.innerHTML = root.innerHTML;
        results.hidden = false;
        if (empty) empty.hidden = true;
        lastQuery = q;
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.warn('[lv-search]', error);
        showFallback(q);
      }
    };

    // The suggestions failed (offline, 429): never leave the previous query's
    // results on screen; offer the search page for what is typed (audit 10-05).
    function showFallback(q) {
      if (input.value.trim() !== q) return;
      const link = document.createElement('a');
      link.className = 'lv-sp__all';
      link.href = (dialog.dataset.searchBase || '/search?type=product&q=') + encodeURIComponent(q);
      const label = document.createElement('span');
      label.textContent = (dialog.dataset.allLabel || '[q]').replace('[q]', q);
      link.append(label);
      results.replaceChildren(link);
      results.hidden = false;
      if (empty) empty.hidden = true;
      lastQuery = q;
    }

    input.addEventListener('input', () => {
      const q = input.value.trim();
      clear.hidden = q === '';
      clearTimeout(timer);
      if (!q) {
        controller?.abort();
        showEmpty();
        return;
      }
      if (q === lastQuery) return;
      timer = setTimeout(() => fetchResults(q), DEBOUNCE);
    });

    clear.addEventListener('click', () => {
      input.value = '';
      controller?.abort();
      showEmpty();
      input.focus();
    });

    // Esc closes the panel in one press (a search field would first clear itself).
    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      dialog.close();
    });

    // Arrow keys move through results; Enter on a highlighted row follows it.
    input.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const items = [...dialog.querySelectorAll(results.hidden ? '.lv-sp__empty a' : '[data-lv-search-item]')].filter(
        (el) => el.offsetParent !== null
      );
      if (!items.length) return;
      event.preventDefault();
      const current = items.findIndex((el) => el === document.activeElement);
      const next = event.key === 'ArrowDown' ? Math.min(items.length - 1, current + 1) : current - 1;
      if (next < 0) input.focus();
      else items[next].focus();
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      if (event.target === input) return;
      const items = [...dialog.querySelectorAll(results.hidden ? '.lv-sp__empty a' : '[data-lv-search-item]')].filter(
        (el) => el.offsetParent !== null
      );
      const current = items.indexOf(event.target);
      if (current === -1) return;
      event.preventDefault();
      const next = event.key === 'ArrowDown' ? Math.min(items.length - 1, current + 1) : current - 1;
      if (next < 0) input.focus();
      else items[next].focus();
    });

    // Clicks on the dimmed backdrop land on the <dialog> itself.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
      if (event.target.closest('[data-lv-search-close]')) dialog.close();
    });

    dialog.addEventListener('close', () => {
      lock(false);
      document.querySelectorAll('[data-lv-search-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    });

    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-lv-search-open]');
      if (!opener) return;
      event.preventDefault();
      document.getElementById('lv-mm')?.open && document.getElementById('lv-mm').close();
      if (dialog.open) return;
      setPlaceholder();
      dialog.showModal();
      opener.setAttribute('aria-expanded', 'true');
      lock(true);
      input.focus();
      input.select();
    });
  };

  // ---------------------------------------------------------------------------

  const init = () => {
    watchMenus();
    setTimeout(syncMenuStyle, 500);
    bindMobileDialog();
    bindSearch();
    updateScrolled();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();

// Lalive: the mobile menu's language switch returns to the same page with its
// query (search terms, filters). Liquid's request.path has no query string
// (audit 10-05).
document.addEventListener('submit', (event) => {
  const form = event.target.closest && event.target.closest('form.lv-mm__lang');
  const field = form && form.querySelector('[name="return_to"]');
  if (field) field.value = location.pathname + location.search;
}, true);
